import React, { useState, useEffect } from 'react';
import { Tag, Search, Plus, FileText, Calendar, TrendingUp, ChevronRight, Hash } from 'lucide-react';
import { Link } from 'react-router-dom';
import { superAdminAPI } from '../../services/api';
import { showErrorToast } from '../../utils/errorHandling';
import { CardLoadingSpinner } from '../ui/LoadingSpinner';
import ErrorMessage from '../ui/ErrorMessage';

const Tags = () => {
  const [tags, setTags] = useState([]);
  const [tagStats, setTagStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    loadTags();
  }, []);

  const loadTags = async () => {
    try {
      setLoading(true);
      setError(null);

      // Load available tags
      const tagsResponse = await superAdminAPI.getQuestionTags();
      const availableTags = tagsResponse.data || [];
      
      // Load questions to calculate tag statistics
      const questionsResponse = await superAdminAPI.getContentGeneratorQuestions({ size: 1000 });
      const questions = questionsResponse.data?.content || [];
      
      // Calculate statistics for each tag
      const stats = {};
      availableTags.forEach(tag => {
        const questionsWithTag = questions.filter(q => 
          q.tags && Array.isArray(q.tags) && q.tags.includes(tag)
        );
        
        stats[tag] = {
          totalQuestions: questionsWithTag.length,
          subjects: [...new Set(questionsWithTag.map(q => q.subject).filter(Boolean))],
          topics: [...new Set(questionsWithTag.map(q => q.topic).filter(Boolean))],
          difficulties: [...new Set(questionsWithTag.map(q => q.difficultyLevel).filter(Boolean))],
          recentQuestions: questionsWithTag
            .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))
            .slice(0, 3)
        };
      });

      setTags(availableTags);
      setTagStats(stats);
    } catch (error) {
      console.error('Error loading tags:', error);
      setError(error);
      showErrorToast(error, 'Failed to load tags');
    } finally {
      setLoading(false);
    }
  };

  const filteredTags = tags.filter(tag =>
    tag.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const getTagColor = (tag) => {
    // Generate consistent colors based on tag name
    const colors = [
      'bg-blue-100 text-blue-800 border-blue-200',
      'bg-green-100 text-green-800 border-green-200',
      'bg-purple-100 text-purple-800 border-purple-200',
      'bg-yellow-100 text-yellow-800 border-yellow-200',
      'bg-pink-100 text-pink-800 border-pink-200',
      'bg-indigo-100 text-indigo-800 border-indigo-200',
      'bg-red-100 text-red-800 border-red-200',
      'bg-orange-100 text-orange-800 border-orange-200'
    ];
    
    const hash = tag.split('').reduce((a, b) => {
      a = ((a << 5) - a) + b.charCodeAt(0);
      return a & a;
    }, 0);
    
    return colors[Math.abs(hash) % colors.length];
  };

  if (loading) {
    return (
      <div className="p-6">
        <CardLoadingSpinner message="Loading tags..." />
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="border-b border-gray-200 pb-4">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 flex items-center">
              <Hash className="h-7 w-7 mr-2 text-blue-600" />
              Question Tags
            </h1>
            <p className="text-gray-600 mt-1">
              Browse and manage question tags for better organization
            </p>
          </div>
          <div className="flex items-center space-x-3">
            <Link
              to="/questions/create"
              className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors flex items-center"
            >
              <Plus className="h-4 w-4 mr-2" />
              Create Question
            </Link>
          </div>
        </div>
      </div>

      {/* Search */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
        <input
          type="text"
          placeholder="Search tags..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
        />
      </div>

      {/* Error State */}
      {error && (
        <ErrorMessage 
          error={error} 
          onRetry={loadTags}
          className="mb-6"
        />
      )}

      {/* Tags Grid */}
      {filteredTags.length === 0 ? (
        <div className="text-center py-12">
          <Hash className="h-12 w-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">
            {searchTerm ? 'No tags found' : 'No tags available'}
          </h3>
          <p className="text-gray-600 mb-6">
            {searchTerm 
              ? 'Try adjusting your search terms'
              : 'Start creating questions to build your tag collection'
            }
          </p>
          <Link
            to="/questions/create"
            className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            <Plus className="h-4 w-4 mr-2" />
            Create First Question
          </Link>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredTags.map((tag) => {
            const stats = tagStats[tag] || {};
            const colorClass = getTagColor(tag);
            
            return (
              <div key={tag} className="bg-white rounded-lg border border-gray-200 hover:border-gray-300 transition-colors">
                <div className="p-6">
                  {/* Tag Header */}
                  <div className="flex items-center justify-between mb-4">
                    <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium border ${colorClass}`}>
                      <Hash className="h-3 w-3 mr-1" />
                      {tag}
                    </span>
                    <span className="text-sm text-gray-500">
                      {stats.totalQuestions || 0} questions
                    </span>
                  </div>

                  {/* Statistics */}
                  <div className="space-y-3 mb-4">
                    {stats.subjects && stats.subjects.length > 0 && (
                      <div>
                        <div className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">
                          Subjects
                        </div>
                        <div className="flex flex-wrap gap-1">
                          {stats.subjects.slice(0, 3).map((subject, index) => (
                            <span key={index} className="text-xs bg-gray-100 text-gray-700 px-2 py-1 rounded">
                              {subject}
                            </span>
                          ))}
                          {stats.subjects.length > 3 && (
                            <span className="text-xs text-gray-500">
                              +{stats.subjects.length - 3} more
                            </span>
                          )}
                        </div>
                      </div>
                    )}

                    {stats.difficulties && stats.difficulties.length > 0 && (
                      <div>
                        <div className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">
                          Difficulty Levels
                        </div>
                        <div className="flex flex-wrap gap-1">
                          {stats.difficulties.map((difficulty, index) => (
                            <span key={index} className="text-xs bg-gray-100 text-gray-700 px-2 py-1 rounded">
                              {difficulty}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Action Buttons */}
                  <div className="flex space-x-2">
                    <Link
                      to={`/questions/create?tag=${encodeURIComponent(tag)}`}
                      className="flex-1 bg-blue-600 text-white text-center py-2 px-3 rounded-lg hover:bg-blue-700 transition-colors text-sm font-medium flex items-center justify-center"
                    >
                      <Plus className="h-3 w-3 mr-1" />
                      Add Question
                    </Link>
                    <Link
                      to={`/my-questions?tag=${encodeURIComponent(tag)}`}
                      className="flex-1 bg-gray-100 text-gray-700 text-center py-2 px-3 rounded-lg hover:bg-gray-200 transition-colors text-sm font-medium flex items-center justify-center"
                    >
                      <FileText className="h-3 w-3 mr-1" />
                      View Questions
                    </Link>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Quick Actions */}
      <div className="bg-gray-50 rounded-lg p-6">
        <h3 className="text-lg font-medium text-gray-900 mb-4">Quick Actions</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Link
            to="/questions/create"
            className="flex items-center p-4 border border-gray-200 rounded-lg hover:border-blue-300 hover:bg-blue-50 transition-colors bg-white"
          >
            <Plus className="h-8 w-8 text-blue-600" />
            <div className="ml-3">
              <div className="font-medium text-gray-900">Create New Question</div>
              <div className="text-sm text-gray-600">Add questions with custom tags</div>
            </div>
            <ChevronRight className="h-5 w-5 text-gray-400 ml-auto" />
          </Link>

          <Link
            to="/my-questions"
            className="flex items-center p-4 border border-gray-200 rounded-lg hover:border-purple-300 hover:bg-purple-50 transition-colors bg-white"
          >
            <FileText className="h-8 w-8 text-purple-600" />
            <div className="ml-3">
              <div className="font-medium text-gray-900">My Questions</div>
              <div className="text-sm text-gray-600">View all questions you've created</div>
            </div>
            <ChevronRight className="h-5 w-5 text-gray-400 ml-auto" />
          </Link>
        </div>
      </div>
    </div>
  );
};

export default Tags;
